import debounce from 'lodash-es/debounce';
import React, { useEffect, useRef } from 'react';
import { Localizer } from 'services/Localizer';
import Spinner from 'shared/components/Spinner';
import { useInViewOnce } from 'shared/hooks/useInViewOnce';
import type { TinySliderInstance, TinySliderSettings } from 'tiny-slider/src/tiny-slider';
import uuid from 'uuid/v4';
import { getInitialProductDetailsState } from '../product-details/types';
import RecentlyViewedProduct from './RecentlyViewedProduct';
import useRecentlyViewedProducts from './useRecentlyViewedProducts';
import { sm, md, lg } from 'shared/breakpoints';

interface RecentlyViewedProductsProps {
	/** If this originated from a tab, this will be the ID of that tab */
	tabId?: string;
	displaytitle?: boolean;
}

/**
 * Represents a listing of recently viewed products for a user, found as a "tab" under the product details page.
 * This is not a standard react component and relies on global state to function properly.
 */
export default function RecentlyViewedProducts(props: RecentlyViewedProductsProps) {
	const productDetailsState = getInitialProductDetailsState();
	const recentlyViewedProducts = useRecentlyViewedProducts({ currentProduct: productDetailsState, active: true });
	const currentInstanceId = useRef('recently-viewed-products-slider-' + uuid()).current;
	const sliderRef = useRef<TinySliderInstance | null>(null);

	const { displaytitle = false } = props;

	const [ref, inView] = useInViewOnce({
		threshold: 0,
	});

	const tnsPromise = import('tiny-slider/src/tiny-slider').then((module) => module.tns);
	const oneItemClass = 'roc-product-list--one-item';
	const twoItemClass = 'roc-product-list--two-items';
	const threeItemClass = 'roc-product-list--three-items';
	const fiveItemClass = 'roc-product-list--five-items';

	// Handle debouncing the recreation of tiny-slider on resize
	const handleResizeDebounced = () => {
		initializeSlider();
	};

	const executeResizeDebounced = debounce(handleResizeDebounced, 200);

	// bind tiny-slider on startup or resize
	const initializeSlider = () => {
		if (sliderRef.current) {
			sliderRef.current.destroy();
		}

		(async () => {
			const currentSliderDiv = document.getElementById(currentInstanceId);
			const tns = await tnsPromise;

			const getTinySliderInstance = (props: { itemsToShow: number }) => {
				const options: TinySliderSettings = {
					container: `#${currentInstanceId}`,
					items: props.itemsToShow,
					slideBy: 'page',
					controls: false,
					mouseDrag: true,
					gutter: 15,
					lazyload: true,
					loop: false,
					edgePadding: 88,
				};

				return tns(options);
			};

			if (currentSliderDiv) {
				const trackElement = currentSliderDiv.parentElement;
				const parentRocProductList = currentSliderDiv.closest('.roc-product-list');
				if (trackElement && parentRocProductList) {
					const containerWidth = trackElement.offsetWidth;
					// Different tiny-slider settings based on parent element width.
					// The "responsive" option on tiny slider is based on window width, not container element width, so we can't use it in this case
					if (containerWidth < sm) {
						// 1 item
						parentRocProductList.classList.remove(twoItemClass, threeItemClass, fiveItemClass);
						parentRocProductList.classList.add(oneItemClass);

						sliderRef.current = getTinySliderInstance({ itemsToShow: 1 });
					} else if (containerWidth < md) {
						// 2 items
						parentRocProductList.classList.remove(oneItemClass, threeItemClass, fiveItemClass);
						parentRocProductList.classList.add(twoItemClass);

						sliderRef.current = getTinySliderInstance({ itemsToShow: 2 });
					} else if (containerWidth < lg) {
						// 3 items
						parentRocProductList.classList.remove(oneItemClass, twoItemClass, fiveItemClass);
						parentRocProductList.classList.add(threeItemClass);

						sliderRef.current = getTinySliderInstance({ itemsToShow: 3 });
					} else {
						// 5 items
						parentRocProductList.classList.remove(oneItemClass, twoItemClass, threeItemClass);
						parentRocProductList.classList.add(fiveItemClass);

						sliderRef.current = getTinySliderInstance({ itemsToShow: 5 });
					}
				}
			}

			// if there's a re-render, or possibly hmr, make sure to destroy the instance
			// to avoid memory leaks.
			return () => {
				if (sliderRef.current) {
					sliderRef.current.destroy();
				}
			};
		})();
	};

	// Bind tiny-slider on startup
	useEffect(() => {
		initializeSlider();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView, recentlyViewedProducts]);

	// Set up an event handler to re-create the tiny slider when the window is resized
	useEffect(() => {
		const handleResize = () => {
			executeResizeDebounced();
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [executeResizeDebounced]);

	if (!recentlyViewedProducts || recentlyViewedProducts.length === 0 || !inView) {
		return <div ref={ref} />;
	}

	return (
		<React.Suspense fallback={<Spinner />}>
			<div ref={ref} className="roc-product-list roc-product-list__recently-viewed">
				<div className="roc-container">
					{displaytitle ? (
						<h2 className="roc-product-list__recently-viewed--title">
							{Localizer('RecentlyViewedProducts')}
						</h2>
					) : null}

					<div id={currentInstanceId} className="roc-product-list__track roc-recently-viewed-products">
						{recentlyViewedProducts.map((item, i) => (
							<RecentlyViewedProduct product={item} key={i} useTinySliderLazyLoading={true} />
						))}
					</div>
				</div>
			</div>
		</React.Suspense>
	);
}
